import merge from 'lodash/merge';
import snakeCase from 'lodash/snakeCase';

import { createDashHttpClient, type HttpRequestConfig } from '@/apis/dash.http';
import { createExternalAxiosInstance } from '@/apis/external-apis';
import { env } from '@/env';
import type {
  ActionMetadata as GeneratedActionMetadata,
  AddNewBrandUserRequest,
  BrandCompact,
  BrandPlanTrialDates,
  BrandTagDisplay,
  BrandUser,
  BrandUserExpanded,
  BusinessHoursResponse,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  DHProductSubscription,
  GetApiBrandsBrandIdUsersParams,
  GetApiBrandsParams,
  GetApiOrganizationsOrganizationIdDhProductSubscriptionsParams,
  GetApiUsersParams,
  GetCompanyCustomerInfo,
  HubspotVisitorTokenResponse,
  IntegrationDetails,
  ListApiKeysResponse,
  ListBrandUsers,
  PasswordResetsResponse,
  PublicPlatformConnection,
  SelfIntegrationsResponse,
  StripeCheckoutSession,
  SubscriptionSourceData,
  TaskResponse,
  Token,
  UpdateBrandUserRequest,
  UpsertBusinessHoursRequest,
  UserCompact,
  UserExpanded,
} from '@/generated/auth/model';
import type { ActionMetadata, RawIdentity, Subscriptions } from '@/types/auth.types';
import type { CamelizeKeys } from '@/types/camelize';

const http = createDashHttpClient(env.authApiUrl);
const noAuthHttp = createDashHttpClient(env.authApiUrl, {
  includeAuth: false,
});
const simpleAxios = createExternalAxiosInstance({
  baseURL: env.authApiUrl,
});

export async function getSelf(config: HttpRequestConfig = {}) {
  return http.get<RawIdentity>('/self', {
    ...config,
  });
}

export async function validateApiKey(
  { token }: { token?: string } = {},
  config: HttpRequestConfig = {},
) {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  // Using an external axios instance here since we don't want impersonation or to logout if this fails
  return simpleAxios.get<RawIdentity>('/self', {
    headers,
    ...config,
  });
}

export async function getSelfSubscriptions(
  { token }: { token?: string } = {},
  config: HttpRequestConfig = {},
) {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return http.get<Subscriptions>('/self/subscriptions', {
    headers,
    camelizeKeys: true,
    ...config,
  });
}

export async function getSelfActionMetadata(config: HttpRequestConfig = {}) {
  return http.get<ActionMetadata>('/self/action_metadata', {
    camelizeKeys: true,
    ...config,
  });
}

export interface CreateTokenParams {
  email?: string;
  password?: string;
  oauth?: unknown;
  otpCode?: string;
  ssoToken?: string;
}

export async function createToken(
  { email, password, oauth, otpCode, ssoToken }: CreateTokenParams,
  config: HttpRequestConfig = {},
) {
  return http.post<Token>(
    '/tokens',
    {
      email,
      password,
      oauth,
      otpCode,
      ssoToken,
    },
    merge({ withCredentials: true }, config),
  );
}

export async function destroyToken(config: HttpRequestConfig = {}) {
  return http.delete<void>('/tokens/self', {
    withCredentials: true,
    ...config,
  });
}

export async function getUserDetails(
  { userId }: { userId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<UserExpanded>(`/users/${userId}`, config);
}

export async function updateUser(
  { userId, data }: { userId: number; data: Partial<CamelizeKeys<UserExpanded>> },
  config: HttpRequestConfig = {},
) {
  return http.patch<UserExpanded>(`/users/${userId}`, data, config);
}

export async function resetUserPassword(
  { userId, hash, password }: { userId: number; hash: string; password: string },
  config: HttpRequestConfig = {},
) {
  return noAuthHttp.post<void>(`/users/${userId}/reset_password`, { hash, password }, config);
}

export async function changeUserPassword(
  { userId, password, oldPassword }: { userId: number; password: string; oldPassword: string },
  config: HttpRequestConfig = {},
) {
  return http.post<void>(
    `/users/${userId}/change_password`,
    { password, oldPassword },
    merge({ camelizeKeys: true }, config),
  );
}

export async function getUsers(
  { params }: { params: Partial<CamelizeKeys<GetApiUsersParams>> },
  config: HttpRequestConfig = {},
) {
  return http.get<UserCompact[]>('/users', { ...config, params });
}

export async function getBrands(
  { params }: { params: Partial<CamelizeKeys<GetApiBrandsParams>> },
  config: HttpRequestConfig = {},
) {
  return noAuthHttp.get<BrandCompact[]>('/brands', merge({ params }, config));
}

export async function getBrandIntegrations(
  { brandId }: { brandId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<CamelizeKeys<IntegrationDetails>>(
    `/brands/${brandId}/integrations`,
    merge({ camelizeKeys: true }, config),
  );
}

export async function getUserIntegrations(config: HttpRequestConfig = {}) {
  return http.get<CamelizeKeys<SelfIntegrationsResponse>>(
    `/self/integrations`,
    merge({ camelizeKeys: true }, config),
  );
}

export async function postOAuth(
  { provider, redirectUri, brandId }: { provider: string; redirectUri: string; brandId: number },
  config: HttpRequestConfig = {},
) {
  const providerFormatted = provider === 'pinterest_v5' ? 'pinterest_v5' : snakeCase(provider);
  return http.post(
    `/${providerFormatted}/oauth`,
    {
      redirectUri,
      brandId,
    },
    config,
  );
}

export async function getBrandUsers(
  {
    brandId,
    limit,
    offset = 0,
    email,
    search,
    sort,
    includeRestricted,
  }: { brandId: number } & Partial<CamelizeKeys<GetApiBrandsBrandIdUsersParams>>,
  config: HttpRequestConfig = {},
) {
  const params: Record<string, unknown> = {
    limit,
    offset,
    includeRestricted,
  };
  if (email) {
    params.email = email;
  }
  if (search) {
    params.search = search;
  }
  if (sort) {
    params.sort = sort;
  }
  return http.get<CamelizeKeys<ListBrandUsers>>(
    `/brands/${brandId}/users`,
    merge(
      { camelizeKeys: true },
      {
        params,
      },
      config,
    ),
  );
}

export async function createBrandUser(
  {
    brandId,
    email,
    permissions,
    role,
  }: {
    brandId: number;
    email: string;
    permissions: CamelizeKeys<AddNewBrandUserRequest>['permissions'];
    role: string;
  },
  config: HttpRequestConfig = {},
) {
  return http.post<BrandUser>(
    `/brands/${brandId}/users`,
    {
      email,
      permissions,
      role,
    },
    config,
  );
}

export async function getBrandUser(
  { brandId, userId }: { brandId: number; userId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<BrandUserExpanded>(`/brands/${brandId}/users/${userId}`, config);
}

export async function updateBrandUser(
  {
    brandId,
    userId,
    role,
    permissions,
  }: {
    brandId: number;
    userId: number;
    role: string;
    permissions: CamelizeKeys<UpdateBrandUserRequest>['permissions'];
  },
  config: HttpRequestConfig = {},
) {
  return http.patch<BrandUserExpanded>(
    `/brands/${brandId}/users/${userId}`,
    {
      role,
      permissions,
    },
    config,
  );
}

export async function removeBrandUser(
  { brandId, userId }: { brandId: number; userId: number },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(`/brands/${brandId}/users/${userId}`, config);
}

export async function resendBrandUserInvitation(
  { brandId, userId }: { brandId: number; userId: number },
  config: HttpRequestConfig = {},
) {
  return http.post<void>(`/brands/${brandId}/users/${userId}/resend_invitation`, config);
}

export async function getBrandUserExportUserList(
  { userId }: { userId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<TaskResponse>(`/brands/user/${userId}/exports/user_list`, config);
}

export async function postSamlAssertion(
  { samlRequest, samlRelayState }: { samlRequest: string; samlRelayState: string },
  config: HttpRequestConfig = {},
) {
  return http.post(
    '/saml_assertions',
    {
      samlRequest,
      relayState: samlRelayState,
    },
    config,
  );
}

export async function generateApiKey(
  { brandId, label }: { brandId: number } & CamelizeKeys<CreateApiKeyRequest>,
  config: HttpRequestConfig = {},
) {
  return http.post<CreateApiKeyResponse>(`/brands/${brandId}/api_keys`, { label }, config);
}

export async function getApiKeysForBrand(
  { brandId }: { brandId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<CamelizeKeys<ListApiKeysResponse>[]>(
    `/brands/${brandId}/api_keys`,
    merge({ camelizeKeys: true }, config),
  );
}

export async function deleteApiKeyForBrand(
  { brandId, apiKeyId }: { brandId: number; apiKeyId: number },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(`/brands/${brandId}/api_keys/${apiKeyId}`, config);
}

export async function generatePasswordReset(
  { email }: { email: string },
  config: HttpRequestConfig = {},
) {
  return http.post<void>('password_resets', { email }, config);
}

export async function getPasswordResetStatus(
  { hash, userId }: { hash: string; userId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<PasswordResetsResponse>(
    `password_resets/${hash}`,
    merge({ params: { userId } }, config),
  );
}

export interface GetPlatformConnectionsParams {
  brandIds?: number | number[] | string;
  brandId?: number;
  brandToken?: string;
  platformType?: string;
}

export async function getPlatformConnections(
  { brandIds, brandId, brandToken, platformType }: GetPlatformConnectionsParams,
  config: HttpRequestConfig = {},
) {
  const params: Record<string, unknown> = {};
  if (brandId) {
    params.brandId = brandId;
    params.brandToken = brandToken;
    params.platformType = platformType;
  } else if (brandIds) {
    params.brandIds = Array.isArray(brandIds) ? brandIds.join(',') : brandIds;
  }
  const axiosInstance = brandToken ? noAuthHttp : http;
  return axiosInstance.get<PublicPlatformConnection[]>(
    `/platform_connections`,
    merge({ params }, config),
  );
}

export interface CreatePlatformConnectionParams {
  brandId: number;
  oauth?: unknown;
  brandToken?: string;
  platformType: string;
  invitedBy?: string;
  sourceCreatorId?: number;
  creatorHandleName?: string;
  page?: unknown;
}

export async function createPlatformConnection(
  {
    brandId,
    oauth,
    brandToken,
    platformType,
    invitedBy,
    sourceCreatorId,
    creatorHandleName,
    page,
  }: CreatePlatformConnectionParams,
  config: HttpRequestConfig = {},
) {
  const axiosInstance = brandToken ? noAuthHttp : http;
  return axiosInstance.post<PublicPlatformConnection>(
    `/platform_connections`,
    {
      brandId,
      oauth,
      brandToken,
      platformType,
      invitedBy,
      page,
      // Used in TikTok creator OAuth flow
      sourceCreatorId,
      creatorHandleName,
    },
    config,
  );
}

export interface UpdatePlatformConnectionParams {
  connectionId: number;
  brandId: number;
  oauth?: unknown;
  brandToken?: string;
  platformType: string;
  invitedBy?: string;
}

export async function updatePlatformConnection(
  {
    connectionId,
    brandId,
    oauth,
    brandToken,
    platformType,
    invitedBy,
  }: UpdatePlatformConnectionParams,
  config: HttpRequestConfig = {},
) {
  const axiosInstance = brandToken ? noAuthHttp : http;
  return axiosInstance.patch<PublicPlatformConnection>(
    `/platform_connections/${connectionId}`,
    { brandId, oauth, brandToken, platformType, invitedBy },
    config,
  );
}

export async function deletePlatformConnection(
  { connectionId }: { connectionId: number },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(`/platform_connections/${connectionId}`, config);
}

export interface GetAccountsParams {
  brandId: number;
  oauth?: unknown;
  brandToken?: string;
  platformType: string;
  invitedBy?: string;
}

export async function getAccounts(
  { brandId, oauth, brandToken, platformType, invitedBy }: GetAccountsParams,
  config: HttpRequestConfig = {},
) {
  const axiosInstance = brandToken ? noAuthHttp : http;
  return axiosInstance.post<void>(
    `/platform_connections/accounts`,
    { brandId, oauth, brandToken, platformType, invitedBy },
    merge({ camelizeKeys: true }, config),
  );
}

export async function createBillingCheckoutSession(
  {
    brandId,
    priceLookupKey,
    redirectDomain,
  }: {
    brandId: number;
    priceLookupKey: string;
    redirectDomain: string;
  },
  config: HttpRequestConfig = {},
) {
  return http.post<CamelizeKeys<StripeCheckoutSession>>(
    `/billing/checkout_sessions`,
    { brandId, priceLookupKey, redirectDomain },
    merge({ camelizeKeys: true }, config),
  );
}

export async function setBrandTrialDates(
  { brandId, dhProductSubscriptionId }: { brandId: number; dhProductSubscriptionId: number },
  config: HttpRequestConfig = {},
) {
  return http.post<CamelizeKeys<BrandPlanTrialDates>>(
    `/brands/${brandId}/trial`,
    { dhProductSubscriptionId },
    merge({ camelizeKeys: true }, config),
  );
}

export async function setActionMetadata(
  {
    brandId,
    actionContext,
    payload,
  }: {
    brandId: number;
    actionContext: string;
    payload: Record<string, unknown>;
  },
  config: HttpRequestConfig = {},
) {
  return http.patch<GeneratedActionMetadata>(
    `/brands/${brandId}/action_metadata/${actionContext}`,
    payload,
    merge({ camelizeKeys: false }, config),
  );
}

export async function getBrandTags(
  { organizationId }: { organizationId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<CamelizeKeys<BrandTagDisplay>[]>(
    `/organizations/${organizationId}/brand_tags`,
    merge({ camelizeKeys: true }, config),
  );
}

export async function createBrandTag(
  { organizationId, name, color }: { organizationId: number; name: string; color: string },
  config: HttpRequestConfig = {},
) {
  return http.post<CamelizeKeys<BrandTagDisplay>>(
    `/organizations/${organizationId}/brand_tags`,
    { name, color },
    merge({ camelizeKeys: true }, config),
  );
}

export async function createBrandTagAssociations(
  {
    organizationId,
    brandIds,
    brandTagIds,
  }: {
    organizationId: number;
    brandIds: number[];
    brandTagIds: number[];
  },
  config: HttpRequestConfig = {},
) {
  const requests: Promise<unknown>[] = [];
  const chunkSize = 10;
  for (let i = 0; i < brandIds.length; i += chunkSize) {
    const brandIdsChunk = brandIds.slice(i, i + chunkSize);
    requests.push(
      http.post(
        `/organizations/${organizationId}/brand_associations`,
        { brandIds: brandIdsChunk, brandTagIds },
        merge({ camelizeKeys: true }, config),
      ),
    );
  }
  await Promise.all(requests);
  return true;
}

export async function removeTagFromBrand(
  {
    organizationId,
    brandId,
    brandTagId,
  }: {
    organizationId: number;
    brandId: number;
    brandTagId: number;
  },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(
    `/organizations/${organizationId}/brands/${brandId}/tags/${brandTagId}`,
    config,
  );
}

export async function deleteBrandTag(
  { organizationId, brandTagId }: { organizationId: number; brandTagId: number },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(`/organizations/${organizationId}/brand_tags/${brandTagId}`, config);
}

export async function updateSubscriptionItem(
  {
    organizationId,
    priceLookupKey,
    subscriptionId = null,
  }: {
    organizationId: number;
    priceLookupKey: string;
    subscriptionId?: number | null;
  },
  config: HttpRequestConfig = {},
) {
  const data: { priceLookupKey: string; subscriptionId?: number | null } = { priceLookupKey };
  if (subscriptionId) {
    data.subscriptionId = subscriptionId;
  }
  return http.post<void>(
    `/organizations/${organizationId}/subscription_items`,
    data,
    merge({ camelizeKeys: true }, config),
  );
}

export async function getOrganizationSubscriptions(
  {
    organizationId,
    params = { brandId: null },
  }: {
    organizationId: number;
    params?: CamelizeKeys<GetApiOrganizationsOrganizationIdDhProductSubscriptionsParams>;
  },
  config: HttpRequestConfig = {},
) {
  return http.get<CamelizeKeys<DHProductSubscription>[]>(
    `/organizations/${organizationId}/dh_product_subscriptions`,
    merge({ camelizeKeys: true }, { params }, config),
  );
}

export async function getSubscriptionSourceData(
  { organizationId, sourceIds }: { organizationId: number; sourceIds: number[] },
  config: HttpRequestConfig = {},
) {
  return http.get<CamelizeKeys<SubscriptionSourceData>[]>(
    `/organizations/${organizationId}/subscription_source_data`,
    merge({ camelizeKeys: true }, { params: { sourceIds: sourceIds.join(',') } }, config),
  );
}

export async function getCompanyCustomerInformation(
  companyId: number,
  config: HttpRequestConfig = {},
) {
  return http.get<CamelizeKeys<GetCompanyCustomerInfo>>(
    `/companies/${companyId}/customer_information`,
    merge({ camelizeKeys: true }, config),
  );
}

export async function createTrialSubscription(
  {
    organizationId = null,
    brandId = null,
    dhProductLabel,
  }: {
    organizationId?: number | null;
    brandId?: number | null;
    dhProductLabel: string;
  },
  config: HttpRequestConfig = {},
) {
  return http.post<CamelizeKeys<DHProductSubscription>>(
    `/dh_product_subscriptions/start_trial`,
    { brandId, organizationId, dhProductLabel },
    merge({ camelizeKeys: true }, config),
  );
}

export async function getHubspotSupportToken(config: HttpRequestConfig = {}) {
  return http.post<CamelizeKeys<HubspotVisitorTokenResponse>>(
    `/self/hubspot_visitor_token`,
    merge({ camelizeKeys: true }, config),
  );
}

export async function getBusinessHours(
  { brandIds }: { brandIds: number[] | string },
  config: HttpRequestConfig = {},
) {
  const params = { brandIds: Array.isArray(brandIds) ? brandIds.join(',') : brandIds };
  return http.get<CamelizeKeys<BusinessHoursResponse>[]>(
    '/business_hours',
    merge({ params, camelizeKeys: true }, config),
  );
}

export async function createBusinessHours(
  { brandIds, hoursConfig, timezone }: CamelizeKeys<UpsertBusinessHoursRequest>,
  config: HttpRequestConfig = {},
) {
  return http.post<CamelizeKeys<BusinessHoursResponse>[]>(
    '/business_hours',
    { brandIds, hoursConfig, timezone },
    merge({ camelizeKeys: true }, config),
  );
}

export async function updateBusinessHours(
  { brandIds, hoursConfig, timezone }: CamelizeKeys<UpsertBusinessHoursRequest>,
  config: HttpRequestConfig = {},
) {
  return http.put<CamelizeKeys<BusinessHoursResponse>[]>(
    '/business_hours',
    { brandIds, hoursConfig, timezone },
    merge({ camelizeKeys: true }, config),
  );
}
